import { useEffect, useRef, useState } from 'react';

interface UseProgressBarOptions {
  /** Whether loading is in progress */
  isLoading: boolean;
  /** Duration in ms to show 100% before hiding (default: 500) */
  completionDelay?: number;
  /** Interval in ms for progress updates (default: 100) */
  updateInterval?: number;
}

interface UseProgressBarReturn {
  /** Current progress value (0-100) */
  progress: number;
  /** Whether the progress bar should be visible */
  showProgress: boolean;
}

/**
 * Hook for managing a fake progress bar that simulates loading progress.
 * Progress increases randomly until 99%, then jumps to 100% when loading completes.
 */
export const useProgressBar = ({
  isLoading,
  completionDelay = 500,
  updateInterval = 100,
}: UseProgressBarOptions): UseProgressBarReturn => {
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const wasLoadingRef = useRef(false);

  useEffect(() => {
    // Detect when loading finishes: show 100% briefly
    if (wasLoadingRef.current && !isLoading) {
      setProgress(100);
      const timeout = setTimeout(() => {
        setShowProgress(false);
        setProgress(0);
      }, completionDelay);
      wasLoadingRef.current = false;
      return () => clearTimeout(timeout);
    }

    wasLoadingRef.current = isLoading;

    if (!isLoading) {
      return;
    }

    setShowProgress(true);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 99) return prev;
        // Random increment that decreases as we approach 99
        const remaining = 99 - prev;
        const increment = Math.random() * Math.min(remaining * 0.1, 5);
        return Math.min(prev + increment, 99);
      });
    }, updateInterval);

    return () => clearInterval(interval);
  }, [isLoading, completionDelay, updateInterval]);

  return { progress, showProgress };
};
